'use client';

import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { createBrowserClient } from '@supabase/ssr';
import { User } from '@supabase/supabase-js';
import { Loader2Icon } from 'lucide-react';
import posthog from 'posthog-js';

import { env } from '@/lib/env';
import { supabaseKey, supabaseUrl } from '@/utils/supabase/env';

export interface AuthUser {
  email?: string;
}

interface Context {
  user: AuthUser | null;
  token: string | null;
  isAdmin: boolean;
  setIsAdmin: (isAdmin: boolean) => void;
  isSuperuser: boolean;
  /**
   * isActuallySuperuser is the actual superuser status, this should only be used in the component
   * that controls whether superuser view is enabled or disabled. For all other cases use isSuperuser.
   */
  isActuallySuperuser: boolean;
  toggleSuperuserView: () => void;
  isPermissionCovered: (permission: 'admin' | 'superuser') => boolean;
}

const Context = createContext<Context | null>(null);

export function useAuthCtx() {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error('useAuthCtx must be used within a AuthProvider');
  }
  return ctx;
}

export function AuthProvider({ children }: PropsWithChildren) {
  const { user, token } = useSupabaseAuth();
  const [isActuallySuperuser, setIsActuallySuperuser] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const isPermissionCovered = useCallback(
    (permission: 'admin' | 'superuser') => {
      if (permission === 'admin') {
        // TODO(SOL-705): remove this once we don't override role from FR (i.e. once Ed is admin)
        // return isAdmin || isSuperuser;
        return true;
      }
      return isSuperuser;
    },
    [isSuperuser]
  );

  const toggleSuperuserView = useCallback(() => {
    if (isActuallySuperuser) {
      setIsSuperuser((isSuperuser) => !isSuperuser);
    }
  }, [isActuallySuperuser]);

  useEffect(() => {
    if (!user) return;
    // Admin gate aligned with the backend (`@solea.ai` OR `EmployeeRole.ADMIN`).
    //
    // - `@solea.ai`: same shortcut the backend uses.
    // - `EmployeeRole.ADMIN`: granted by `EmployeeMeProvider` via setIsAdmin once
    //   the `me` endpoint loads — that path covers every non-@solea.ai admin.
    // - `env.IS_DEV`: local convenience only. Backend will still 403 if the
    //   underlying user isn't actually admin, so this never grants real access.
    if (user.email?.endsWith('@solea.ai') || env.IS_DEV) {
      setIsAdmin(true);
      setIsSuperuser(true);
      setIsActuallySuperuser(true);
    }
    posthog.identify(user.id, { email: user.email });
  }, [user]);

  if (!token) {
    return (
      <div className='flex h-screen w-full items-center justify-center'>
        <div className='flex animate-pulse flex-col items-center gap-2'>
          <Loader2Icon className='animate-spin' />
          <div className='text-xs font-medium text-muted-foreground'>Authenticating</div>
        </div>
      </div>
    );
  }

  return (
    <Context.Provider
      value={{
        user,
        token,
        isSuperuser,
        isAdmin,
        setIsAdmin,
        isPermissionCovered,
        isActuallySuperuser,
        toggleSuperuserView,
      }}
    >
      {children}
    </Context.Provider>
  );
}

function useSupabaseAuth() {
  const router = useRouter();
  const [supabase] = useState(() => {
    return createBrowserClient(supabaseUrl, supabaseKey);
  });
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const hasRunOnce = useRef(false);
  useEffect(() => {
    if (!hasRunOnce.current) {
      hasRunOnce.current = true;
      supabase.auth.getSession().then(({ data: { session } }) => {
        setToken(session?.access_token ?? null);
        setUser(session?.user ?? null);
        if (!session?.access_token) {
          console.log('User session not found, redirecting to login');
          router.push('/login');
        }
      });
    }

    supabase.auth.startAutoRefresh();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        console.log('auth state change', event);
        setToken(session.access_token ?? null);
        setUser(session.user ?? null);
      }
    });

    return () => {
      subscription.unsubscribe();
      supabase.auth.stopAutoRefresh();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  return { user, token };
}
